import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import i18n from "i18next";
import { requiredNonNegativeFloatRule, requireRule } from "src/components/common/forms/validation.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField";
import { AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import { SalesPart } from "src/api/generated/erp/db/types/tables/salesPart.ts";
import { SalesPartEditApi_InitData } from "src/api/generated/erp/sales/masterData/api/salesPartEditApi.ts";
import { getConfigurationDefaultPricingMethodLabels } from "src/api/generated/erp/db/types/enums/configurationDefaultPricingMethod.ts";
import {
	getSalesPartTypeLabel,
	SalesPartType,
	SalesPartTypeValues,
} from "src/api/generated/erp/db/types/enums/salesPartType.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { FetchAsyncOptionParams } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faBoxes, faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SalesPartContentLinesDataGrid } from "src/components/views/erp/sales/masterData/salesParts/SalesPartContentLinesDataGrid.tsx";
import { useFieldArray } from "react-hook-form";
import { FormSection } from "src/components/common/forms/FormSection.tsx";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { SalesPartImportCode } from "src/api/generated/erp/db/types/tables/salesPartImportCode.ts";
import {
	ObjectAccountingForm,
	ObjectAccountingFormValues,
} from "src/components/views/erp/common/accounting/ObjectAccountingForm.tsx";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesPartFormContentProps extends AavoFormContentParams<SalesPartFormValues> {
	salesPartId: number | null;
	salesPartNoRequired?: boolean;
	part: Partial<Pick<PartView, "acquisitionMethod" | "isConfigurable">> | null | undefined;
	initData: Pick<
		SalesPartEditApi_InitData,
		"salesPartCategoryOptions" | "salesPriceGroupOptions" | "erpUnitOptions" | "vatCodeOptions"
	>;
}

export interface SalesPartFormValues
	extends Omit<SalesPart, "salesPartId" | "lastUpdated" | "salesPartLongName">, ObjectAccountingFormValues {
	importCodes: Pick<SalesPartImportCode, "importCode">[];
}

export const SalesPartFormContent = ({
	salesPartId,
	salesPartNoRequired = true,
	initData: { salesPartCategoryOptions, salesPriceGroupOptions, erpUnitOptions, vatCodeOptions },
	control,
	watch,
	setValue,
	part,
}: SalesPartFormContentProps) => {
	const { openDialog } = useGenericDialog();
	const { userSites } = useGlobalInitData();

	const isNewRecord = salesPartId == null;
	const isSplitBomPart = part?.acquisitionMethod === "SPLIT_BOM";
	const salesPartType = watch("salesPartType");

	return (
		<>
			<FormSelectField
				control={control}
				name={"salesPartType"}
				label={i18n.t("type")}
				options={getSalesPartTypeOptions()}
				getOptionKey={(o) => o}
				getOptionLabel={(o) => getSalesPartTypeLabel(o)}
				rules={requireRule()}
				onChange={async (value) => {
					setValue("isDefault", value === "WAREHOUSE");
				}}
				disableClearable
			/>
			{part == null && (
				<>
					<FormSelectField
						control={control}
						name={"siteId"}
						label={i18n.t("site")}
						options={userSites}
						getOptionKey={(site) => site.siteId}
						getOptionLabel={(site) => site.siteName}
						rules={requireRule()}
						disabled={salesPartId != undefined}
						disableClearable
					/>
					{salesPartType === "WAREHOUSE" && (
						<FormAsyncSelectField
							control={control}
							name={"partId"}
							label={i18n.t("part")}
							fetchOptions={({ searchQuery, currentSelection }: FetchAsyncOptionParams<number>) =>
								SitePartQueryApi.getPartSelectionOptions({
									currentPartId: currentSelection,
									searchQuery: searchQuery,
									siteId: watch("siteId"),
								})
							}
							getOptionKey={(o) => o.partId}
							getOptionLabel={(o) => o.partLongName}
							disabled={salesPartId != undefined}
							rules={requireRule()}
						/>
					)}
				</>
			)}
			<FormTextField
				control={control}
				name="salesPartNo"
				label={i18n.t("sales_part_no")}
				rules={salesPartNoRequired ? requireRule() : undefined}
			/>
			<FormTextField
				control={control}
				name="salesPartDescription"
				autoFocus
				label={i18n.t("description")}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"salesPartCategoryId"}
				label={i18n.t("sales_part_category")}
				options={salesPartCategoryOptions}
				getOptionKey={(o) => o.salesPartCategoryId}
				getOptionLabel={(o) => o.name ?? ""}
			/>
			<FormSelectField
				control={control}
				name={"salesPartPriceGroupId"}
				label={i18n.t("sales_price_group")}
				options={salesPriceGroupOptions}
				getOptionKey={(o) => o.salesPartPriceGroupId}
				getOptionLabel={(o) => o.name ?? ""}
			/>
			<FormNumberField
				control={control}
				name="salesPrice"
				label={i18n.t("sales_price")}
				rules={requiredNonNegativeFloatRule()}
			/>
			<FormSelectField
				control={control}
				name={"salesPriceUnitId"}
				label={i18n.t("sales_price_unit")}
				options={erpUnitOptions}
				getOptionKey={(o) => o.unitId}
				getOptionLabel={(o) => o.unitName}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"salesUnitId"}
				label={i18n.t("sales_unit")}
				options={erpUnitOptions}
				getOptionKey={(o) => o.unitId}
				getOptionLabel={(o) => o.unitName}
				rules={requireRule()}
			/>
			<FormNumberField
				control={control}
				name="priceFactor"
				label={i18n.t("price_factor")}
				rules={requiredNonNegativeFloatRule((value) =>
					value > 0 ? true : i18n.t("number_must_be_greater_than_zero"),
				)}
			/>
			<FormNumberField
				control={control}
				name="salesFactor"
				label={i18n.t("sales_factor")}
				rules={requiredNonNegativeFloatRule((value) =>
					value > 0 ? true : i18n.t("number_must_be_greater_than_zero"),
				)}
			/>
			<FormSelectField
				control={control}
				name={"vatCodeId"}
				label={i18n.t("vat_code")}
				options={vatCodeOptions}
				getOptionKey={(o) => o.vatCodeId}
				getOptionLabel={(o) => o.vatCodeName}
				rules={requireRule()}
			/>
			{part?.isConfigurable && (
				<FormEnumSelectField
					control={control}
					name={"configurationDefaultPricingMethod"}
					label={i18n.t("configuration_pricing_method")}
					options={getConfigurationDefaultPricingMethodLabels()}
					rules={requireRule()}
				/>
			)}
			{salesPartId != null && salesPartType === "PACKAGE_" && part?.acquisitionMethod !== "SPLIT_BOM" && (
				<AsyncButton
					icon={faBoxes}
					variant={"outlined"}
					label={i18n.t("package_lines")}
					onClick={async () => {
						openDialog({
							title: i18n.t("package_lines"),
							size: "lg",
							content: <SalesPartContentLinesDataGrid parentSalesPartId={salesPartId} />,
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.masterData.salesPartPackageContent.salesPartFormContent.dialog"),
								title: i18n.t("package_lines"),
								pathSegments: [i18n.t("package_lines")],
								data: { parentSalesPartId: salesPartId },
							},
						});
					}}
				/>
			)}
			<ObjectAccountingForm control={control} />
			{watch("partId") != null && (
				<FormCheckbox
					control={control}
					name={"isDefault"}
					label={i18n.t("part_default_sales_part")}
					disabled={!isNewRecord}
					startNewGridRow
				/>
			)}
			<FormCheckbox control={control} name={"active"} label={i18n.t("active")} startNewGridRow />
			<FormCheckbox
				control={control}
				name={"canBeUsedOnCustomerOrderLine"}
				label={i18n.t("can_be_used_on_customer_order_line")}
				startNewGridRow
			/>
			<FormCheckbox
				control={control}
				name={"canBeUsedOnCustomerOfferLine"}
				label={i18n.t("can_be_used_on_customer_offer_line")}
				startNewGridRow
			/>
			{watch("canBeUsedOnCustomerOfferLine") && !watch("canBeUsedOnCustomerOrderLine") && (
				<FormCheckbox
					control={control}
					name={"isAdjustment"}
					label={i18n.t("is_adjustment_line")}
					helperText={i18n.t("is_adjustment_line_explanation")}
					startNewGridRow
				/>
			)}
			<FormCheckbox
				control={control}
				name={"hasTemplateConfiguration"}
				label={i18n.t("has_template_configuration")}
				startNewGridRow
			/>
			{(salesPartType === "SERVICE" || part?.acquisitionMethod === "NO_ACQUISITION") && (
				<FormCheckbox
					control={control}
					name={"automaticDelivery"}
					label={i18n.t("automatic_delivery")}
					helperText={i18n.t("sales_part_automatic_delivery_explanation")}
					startNewGridRow
				/>
			)}
			<ImportCodesForm control={control} />
		</>
	);

	function getSalesPartTypeOptions(): SalesPartType[] {
		if (part != null) {
			if (isSplitBomPart) {
				return ["PACKAGE_"];
			} else {
				return ["WAREHOUSE"];
			}
		}
		if (!isNewRecord) return [salesPartType];
		return [...SalesPartTypeValues];
	}
};

interface ImportCodesFormProps {
	control: SalesPartFormContentProps["control"];
}

const ImportCodesForm = ({ control }: ImportCodesFormProps) => {
	const importCodesFieldArray = useFieldArray({
		control,
		name: "importCodes",
	});

	return (
		<FormSection
			label={i18n.t("import_codes")}
			sx={{
				display: "flex",
				flexDirection: "column",
			}}
		>
			{importCodesFieldArray.fields.map((field, index) => (
				<HorizontalBox key={field.id}>
					<FormTextField
						control={control}
						name={`importCodes.${index}.importCode`}
						label={i18n.t("code")}
						rules={requireRule()}
						startNewGridRow
					/>
					<AavoButton icon={faTimes} onClick={() => importCodesFieldArray.remove(index)} />
				</HorizontalBox>
			))}
			<AavoButton
				icon={faPlus}
				label={i18n.t("add")}
				variant={"outlined"}
				onClick={() =>
					importCodesFieldArray.append({
						importCode: "",
					})
				}
				sx={{
					alignSelf: "flex-start",
				}}
			/>
		</FormSection>
	);
};
